import useBackend from '@/hooks/useBackend'

import config from '@/config'
import axios from 'axios'

const { setup, doGet, doPost } = useBackend()
setup('/masterdata', config.microserviceBaseUrl, {
  responseSuccess: response => {
    return response.data
  },
  responseError: error => {
    return Promise.reject(error)
  },
})

const CancelToken = axios.CancelToken
let cancelFindProviders

import { PRE_INSURER_TYPE, CHANNEL } from '@/config/constants'
import { SortUtil } from '@/utils/Sorting'

export default function useMasterdata() {
  function fetchLocations({ query, ...params }) {
    const payload = {
      q: (query || '').trim(),
      ...params,
    }
    return doGet('/locations', payload)
  }

  function fetchHouseLocation({ housenumber, street, zipcode }) {
    return doGet('/locations/houselocation', { housenumber, street, zipcode })
  }

  function fetchHouseNumbers({ housenumber, street, zipcode }) {
    return doGet('/locations/housenumbers', { housenumber, street, zipcode })
  }

  function fetchReverseLocation({ lat, lng }) {
    return doGet('/locations/reversegeocode', { lat, lng })
  }

  function fetchStreets({ street, zipcode }) {
    return doGet('/locations/streets', { street, zipcode })
  }

  function getHouseLocation({ housenumber, street, zipcode }) {
    return new Promise((resolve, reject) => {
      const params = {
        housenumber,
        street,
        zipcode,
      }

      fetchHouseLocation(params)
        .then(response => resolve(response.data))
        .catch(error => reject(error))
    })

    /* return {
      canton: 'SG',
      country: 'CH',
      displayName: '7320 Sargans',
      houseNumber: '31',
      latitude: '47.05024',
      locationNumber: '3296',
      longitude: '9.43612',
      name: 'Sargans',
      primeRegion: 'SG_2',
      street: 'Ragazerstrasse',
      town: 'Sargans',
      zipCode: '7320',
      zipCodeAddon: '00'
    }*/
  }

  function getHouseNumbers({ housenumber, street, zipcode }) {
    return new Promise((resolve, reject) => {
      const params = {
        housenumber,
        street,
        zipcode,
      }

      fetchHouseNumbers(params)
        .then(response => resolve(response.data))
        .catch(error => reject(error))
    })
  }

  function getStreets({ street, zipcode }) {
    return new Promise((resolve, reject) => {
      const params = {
        street,
        zipcode,
      }

      fetchStreets(params)
        .then(response => resolve(response.data))
        .catch(error => reject(error))
    })
  }

  function searchStreets(street, zipcode) {
    return getStreets({ street, zipcode })
      .then(results => {
        return results
      })
      .catch(error => console.log(error))
  }

  function searchHouseNumbers(housenumber, street, zipcode) {
    return getHouseNumbers({ housenumber, street, zipcode })
      .then(results => {
        return results
      })
      .catch(error => console.log(error))
  }

  function fetchPreinsurers({ channel, contractStartDate, insurerType }) {
    return doGet('/preinsurers', { channel, contractStartDate, insurerType })
  }

  function getPreInsurers(questions) {
    const kvgSet = new Set()
    const vvgSet = new Set()
    questions.forEach(q => {
      if (q.kvg) kvgSet.add(`${q.kvgContractStartDate.substring(0, 7)}-01`)
      if (q.vvg) vvgSet.add(`${q.vvgContractStartDate.substring(0, 7)}-01`)
    })
    const promises = []
    if (kvgSet.size > 0) {
      for (const kvgDate of kvgSet.keys()) {
        promises.push(_getPreInsurersByType(PRE_INSURER_TYPE.KVG, kvgDate))
      }
    }
    if (vvgSet.size > 0) {
      for (const vvgDate of vvgSet.keys()) {
        promises.push(_getPreInsurersByType(PRE_INSURER_TYPE.VVG, vvgDate))
      }
    }
    return Promise.all(promises)
  }

  function _getPreInsurersByType(insurerType, contractStartDate) {
    return fetchPreinsurers({
      channel: CHANNEL.OVP,
      contractStartDate: `${contractStartDate.substring(0, 7)}-01`,
      insurerType,
    }).then(preInsurers => {
      return {
        type: insurerType,
        contractStartDate,
        data: preInsurers.data
          ?.map(item => {
            return {
              value: item.id,
              text: item.name,
            }
          })
          .sort(SortUtil.sortByParam('text')),
      }
    })
  }

  // function fetchFranchises (language) {
  //   return doGet('/franchises', language)
  // }

  // function fetchMovedFromAbroadReasons(language) {
  //   return doGet('/moved-from-abroad', { language })
  // }

  // function fetchNoKVGReasons(language) {
  //   return doGet('/no-kvg-reason', { language })
  // }

  /**
   * Find current care providers / medical office centers
   *
   * interface ProviderPayload {
   *   avmNetworks?: string[]             // AVM doctor networks
   *   contractStartDate: LocalDateTime
   *   limit?: number
   *   name?: string                      // Search query for the doctor name
   * }
   *
   * interface ProviderGeoPayload extends ProviderPayload {
   *   maxLat: number
   *   minLat: number
   *   maxLon: number
   *   minLon: number
   * }
   *
   * interface ProviderZipCodePayload extends ProviderPayload {
   *   zipCode: number
   * }
   *
   * interface ProviderResponse {
   *   data: Provider[]
   * }
   *
   * @param {ProviderGeoPayload | ProviderZipCodePayload} payload
   * @returns {Promise<ProviderResponse>}
   */
  function findProviders({ query, ...params }) {
    if (cancelFindProviders) {
      cancelFindProviders()
    }

    const payload = {
      q: query,
      ...params,
      // An executor function receives a cancel function as a parameter
      cancelToken: new CancelToken(function executor(c) {
        cancelFindProviders = c
      }),
    }
    return doPost('/care-providers', payload)
  }

  return {
    fetchLocations,
    fetchPreinsurers,
    fetchReverseLocation,
    findProviders,
    getHouseLocation,
    getHouseNumbers,
    getPreInsurers,
    getStreets,
    searchStreets,
    searchHouseNumbers,
  }
}
